// WHAT: Weekly progress chart component displaying MiracleFlow session completion by day.
// WHY:  Provides visual representation of daily session completion rates throughout the week, helping users track consistency and identify patterns.
// DOMAIN: Productivity → MiracleFlow
// DEPENDENCIES: React, @/components/ui, lucide-react, @tracq/frontend-shared/utils
// RISKS:
//   - NOTE: Default data assumes 2 max sessions per day (morning + evening); may not match actual session structure.
//   - WARN: Day name calculation uses JavaScript Date.getDay() which returns 0-6 (Sunday-Saturday); ensure dayNames array matches.
//   - PERF: Rendering progress bars for all 7 days is minimal; performance impact is negligible.

import { Calendar, TrendingUp } from "lucide-react";
import React from "react";

import { Card, Stack, Heading, ProgressBar } from "@/components/ui";
import { cn } from "@/shared/utils";

interface WeeklyProgressChartProperties {
  className?: string;
  weeklyData?: { day: string; sessions: number; maxSessions: number }[];
}

/**
 * WHAT:
 *   Weekly progress chart component displaying MiracleFlow session completion by day.
 *
 * WHY:
 *   Provides users with visual representation of daily session completion rates throughout the week.
 *   Helps users track consistency, identify patterns, and stay motivated by seeing their weekly progress.
 *
 * PROPS:
 *   - weeklyData?: Array<{ day: string; sessions: number; maxSessions: number }> - Optional weekly data array
 *   - className?: string - Optional additional CSS classes
 *
 * BEHAVIOR:
 *   - Uses default data (all days with 0 sessions, max 2) if weeklyData is not provided
 *   - Calculates today's day name using JavaScript Date.getDay()
 *   - Highlights today's row with blue color and "Today" label
 *   - Displays progress bar for each day showing completion percentage
 *   - Shows session count (completed/max) for each day
 *   - Calculates and displays total sessions for the week
 *   - Applies color-coded progress states (success >= 100%, default >= 50%, warning < 50%)
 *
 * RISKS:
 *   - NOTE: Default data assumes 2 max sessions per day (morning + evening); may not match actual session structure.
 *   - WARN: Day name calculation uses JavaScript Date.getDay() which returns 0-6 (Sunday-Saturday); ensure dayNames array matches.
 *   - PERF: Rendering progress bars for all 7 days is minimal; performance impact is negligible.
 *   - NOTE: Progress calculation assumes maxSessions > 0; division by zero is handled with fallback to 0.
 *
 * @param root0
 *
 * @param root0.weeklyData
 *
 * @param root0.className
 *
 * @returns JSX.Element
 *
 */
export const WeeklyProgressChart: React.FC<WeeklyProgressChartProperties> = ({
  className,
  weeklyData,
}) => {
  // NOTE: Default data assumes 2 max sessions per day (morning + evening)
  const defaultData = [
    { day: "Mon", maxSessions: 2, sessions: 0 },
    { day: "Tue", maxSessions: 2, sessions: 0 },
    { day: "Wed", maxSessions: 2, sessions: 0 },
    { day: "Thu", maxSessions: 2, sessions: 0 },
    { day: "Fri", maxSessions: 2, sessions: 0 },
    { day: "Sat", maxSessions: 2, sessions: 0 },
    { day: "Sun", maxSessions: 2, sessions: 0 },
  ];

  // NOTE: Use provided data or fallback to defaults
  const data = weeklyData ?? defaultData;

  // NOTE: JavaScript Date.getDay() returns 0 (Sunday) to 6 (Saturday)
  const today = new Date().getDay();
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const todayName = dayNames[today];

  return (
    <Card
      className={cn(
        "bg-[#1E293B] border-[#334155]",
        "hover:border-tracq-bright-blue/50 transition-all duration-300",
        "hover:shadow-tracq-card",
        className,
      )}
      padding="lg"
      variant="default"
    >
      <Stack direction="col" gap={4}>
        <Stack align="center" direction="row" gap={2}>
          <Calendar className="w-5 h-5 text-tracq-bright-blue" />
          <Heading className="text-[#F1F5F9] font-semibold" level="h3" size="md">
            Weekly Progress
          </Heading>
        </Stack>

        <div className="space-y-3">
          {data.map((day) => {
            // NOTE: Calculate completion percentage with division by zero protection
            const percentage = day.maxSessions > 0 ? (day.sessions / day.maxSessions) * 100 : 0;
            // NOTE: Highlight today's row for better visibility
            const isToday = day.day === todayName;

            return (
              <div className="space-y-1" key={day.day}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        "text-sm font-medium w-10",
                        isToday ? "text-tracq-bright-blue" : "text-[#94A3B8]",
                      )}
                    >
                      {day.day}
                    </span>
                    {isToday ? (
                      <span className="text-xs text-tracq-bright-blue font-medium">Today</span>
                    ) : null}
                  </div>
                  <span className="text-sm text-[#94A3B8]">
                    {day.sessions}/{day.maxSessions}
                  </span>
                </div>
                <ProgressBar
                  className="h-2"
                  max={100}
                  size="sm"
                  state={(() => {
                    if (percentage >= 100) {
                      return "success";
                    }
                    if (percentage >= 50) {
                      return "default";
                    }
                    return "warning";
                  })()}
                  value={percentage}
                />
              </div>
            );
          })}
        </div>

        <div className="flex items-center gap-2 text-[#94A3B8] text-sm pt-2 border-t border-[#334155]">
          <TrendingUp className="w-4 h-4 text-tracq-bright-blue" />
          <span>{data.reduce((sum, day) => sum + day.sessions, 0)} sessions this week</span>
        </div>
      </Stack>
    </Card>
  );
};
